import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import apiService from '../../services/api';
import { Button, AdminPageLayout, Section, Badge } from '../../components';
import {
  Listing,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';

const BranchPrograms = () => {
  const { branch_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: ''
  });

  useEffect(() => {
    loadClubPrograms();
  }, []);

  const loadClubPrograms = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getPrograms();
      setPrograms(response.programs || []);
    } catch (err) {
      setError(err.message || 'Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category: '',
      status: ''
    });
  };

  const filteredPrograms = programs.filter(program => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      if (!program.name?.toLowerCase().includes(searchTerm) &&
        !program.description?.toLowerCase().includes(searchTerm) &&
        !program.category?.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    if (filters.category && program.category !== filters.category) {
      return false;
    }
    if (filters.status && program.status !== filters.status) {
      return false;
    }
    return true;
  });

  // Get unique categories and statuses for filter options
  const categories = [...new Set(programs.map(p => p.category).filter(Boolean))];
  const statuses = [...new Set(programs.map(p => p.status).filter(Boolean))];

  const renderProgramItem = (program) => {
    return (
      <ListingItem
        key={program.id}
        title={program.name}
        subtitle={program.description}
        actions={
          <Button variant="secondary" size="sm" disabled>
            <Lock size={16} style={{ marginRight: '6px' }} />
            READ-ONLY
          </Button>
        }
      >
        <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Badge variant="info" size="sm">{program.category}</Badge>
          {program.status && (
            <Badge variant={program.status === 'active' ? 'success' : 'warning'} size="sm">
              {program.status}
            </Badge>
          )}
          <Badge variant="outline" size="sm">
            <Lock size={12} style={{ marginRight: '4px' }} />
            CLUB LEVEL
          </Badge>
        </div>
        <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
            Sport: {program.sport || 'Soccer'}
          </div>
          {(program.min_age || program.max_age) && (
            <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
              Age Range: {program.min_age ? `${program.min_age}+` : 'Any'} - {program.max_age || 'No limit'}
            </div>
          )}
          {program.team_count !== undefined && (
            <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
              Teams: {program.team_count || 0}
            </div>
          )}
          {program.registration_fee !== undefined && (
            <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
              Registration Fee: {program.registration_fee ? `$${program.registration_fee}` : 'Free'}
            </div>
          )}
        </div>
      </ListingItem>
    );
  };

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      searchPlaceholder="Search programs by name, description, or category..."
      filters={[
        {
          name: 'category',
          label: 'Category',
          options: categories.length > 0
            ? [{ value: '', label: 'All Categories' }, ...categories.map(cat => ({ value: cat, label: cat }))]
            : [{ value: '', label: 'All Categories' }]
        },
        {
          name: 'status',
          label: 'Status',
          options: statuses.length > 0
            ? [{ value: '', label: 'All Statuses' }, ...statuses.map(status => ({ value: status, label: status }))]
            : [{ value: '', label: 'All Statuses' }]
        }
      ]}
      activeFilters={{
        category: filters.category,
        status: filters.status
      }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={handleClearFilters}
    />
  );

  return (
    <AdminPageLayout
      title="CLUB PROGRAMS"
      subtitle="View club-level programs (read-only)"
      backPath={`/branch/${branch_id}/dashboard`}
      maxWidth="1400px"
    >
      <Section
        title="AVAILABLE PROGRAMS"
        subtitle={error ? null : `${filteredPrograms.length} ${filteredPrograms.length === 1 ? 'program' : 'programs'} available`}
      >
        {error && (
          <div style={{
            padding: '1rem 1.25rem',
            marginBottom: '1.5rem',
            background: 'var(--color-error-background)',
            border: '1px solid var(--color-error)',
            color: 'var(--color-error)',
            borderRadius: '8px',
            fontSize: '0.95rem'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        <Listing
          items={filteredPrograms}
          renderItem={renderProgramItem}
          loading={loading}
          filters={filterBar}
          emptyMessage="No programs found"
          variant="list"
        />
      </Section>
    </AdminPageLayout>
  );
};

export default BranchPrograms;
